// eslint-disable-next-line no-restricted-imports
import { Recurrence, SnoozeOption, TaskInstanceStatus, TaskStatus } from "../reclaim-api/client";

const SNOOZE_INTERVALS = [
  SnoozeOption.FROMNOW15M,
  SnoozeOption.FROMNOW30M,
  SnoozeOption.FROMNOW1H,
  SnoozeOption.FROMNOW2H,
  SnoozeOption.FROMNOW4H,
  SnoozeOption.TOMORROW,
  SnoozeOption.INTWODAYS,
  SnoozeOption.NEXTWEEK,
] as const;

export const SNOOZE_LABELS: Record<SnoozeOption, string> = {
  [SnoozeOption.FROMNOW15M]: "15min",
  [SnoozeOption.FROMNOW30M]: "30min",
  [SnoozeOption.FROMNOW1H]: "1hr",
  [SnoozeOption.FROMNOW2H]: "2hrs",
  [SnoozeOption.FROMNOW4H]: "4hrs",
  [SnoozeOption.TOMORROW]: "1 day",
  [SnoozeOption.INTWODAYS]: "2 days",
  [SnoozeOption.NEXTWEEK]: "1 week",
};

export type OneOnOneSkipOption = "ANOTHER_DAY" | "ANOTHER_WEEK" | "ANOTHER_TIME";

function getStartOfDay(dt: Date): Date {
  const dayStart = new Date(dt);
  dayStart.setUTCHours(0, 0, 0, 0);
  return dayStart;
}

function taskInstanceIsUpcoming(taskInstanceStatus: TaskInstanceStatus): boolean {
  return taskInstanceStatus === TaskInstanceStatus.ACTIVE || taskInstanceStatus === TaskInstanceStatus.PENDING;
}

export function getSnoozeScentenceEnder(snoozeOption?: SnoozeOption): string {
  switch (snoozeOption) {
    case SnoozeOption.FROMNOW15M:
      return " to 15 minutes from now";
    case SnoozeOption.FROMNOW30M:
      return " to 30 minutes from now";
    case SnoozeOption.FROMNOW1H:
      return " to 1 hour from now";
    case SnoozeOption.FROMNOW2H:
      return " to 2 hours from now";
    case SnoozeOption.FROMNOW4H:
      return " to 4 hours from now";
    case SnoozeOption.TOMORROW:
      return " until tomorrow";
    case SnoozeOption.INTWODAYS:
      return " to 2 days from";
    case SnoozeOption.NEXTWEEK:
      return " until next week";
    default:
      return "";
  }
}

export const ACTION_VISIBILITY_LOGIC = {
  Habits: {
    assignment: {
      start(habitId: number, currentAssignmentId: number | undefined): boolean {
        return !ACTION_VISIBILITY_LOGIC.Habits.assignment.stop(habitId, currentAssignmentId);
      },

      restart(habitId: number, currentAssignmentId: number | undefined): boolean {
        return ACTION_VISIBILITY_LOGIC.Habits.assignment.stop(habitId, currentAssignmentId);
      },

      stop(habitId: number, currentAssignmentId: number | undefined): boolean {
        return habitId === currentAssignmentId;
      },

      edit(): boolean {
        return true;
      },

      reset(exceptions: unknown[] | undefined): boolean {
        return !!exceptions?.length;
      },
    },
    instances: {
      lockOrUnlock(eventStart: Date, now: Date): boolean {
        return eventStart > now;
      },

      skip(): boolean {
        return true;
      },

      // TODO: this really needs to check if that habit
      // is daily too, but recurrence is not easily
      // accessible from where this function is used
      reschedule(eventStart: Date, now: Date): boolean {
        const nowDayStart = getStartOfDay(now);
        return eventStart >= nowDayStart;
      },

      rescheduleType(eventStart: Date, recurrence: Recurrence, now: Date): "quick" | "snoozeOptions" | "none" {
        const nowDayStart = getStartOfDay(now);
        if (eventStart >= nowDayStart) return "snoozeOptions";
        else return recurrence === Recurrence.Daily ? "none" : "quick";
      },

      snoozeOptions(recurrence: Recurrence): readonly SnoozeOption[] {
        switch (recurrence) {
          case Recurrence.Daily:
            return [
              SnoozeOption.FROMNOW15M,
              SnoozeOption.FROMNOW30M,
              SnoozeOption.FROMNOW1H,
              SnoozeOption.FROMNOW2H,
              SnoozeOption.FROMNOW4H,
            ];
          case Recurrence.Weekly:
            return [
              SnoozeOption.FROMNOW15M,
              SnoozeOption.FROMNOW30M,
              SnoozeOption.FROMNOW1H,
              SnoozeOption.FROMNOW2H,
              SnoozeOption.FROMNOW4H,
              SnoozeOption.TOMORROW,
            ];
          case Recurrence.Biweekly:
            return [
              SnoozeOption.FROMNOW15M,
              SnoozeOption.FROMNOW30M,
              SnoozeOption.FROMNOW1H,
              SnoozeOption.FROMNOW2H,
              SnoozeOption.FROMNOW4H,
              SnoozeOption.TOMORROW,
              SnoozeOption.INTWODAYS,
            ];
          case Recurrence.Monthly:
          case Recurrence.Quarterly:
            return [
              SnoozeOption.FROMNOW15M,
              SnoozeOption.FROMNOW30M,
              SnoozeOption.FROMNOW1H,
              SnoozeOption.FROMNOW2H,
              SnoozeOption.FROMNOW4H,
              SnoozeOption.TOMORROW,
              SnoozeOption.INTWODAYS,
              SnoozeOption.NEXTWEEK,
            ];
        }
      },

      markDone(
        habitId: number,
        eventStart: Date,
        eventEnd: Date,
        now: Date,
        currentAssignmentId: number | undefined
      ): boolean {
        // Happening now can mark done.
        if (habitId === currentAssignmentId) return true;

        const eventDayStart = getStartOfDay(eventStart);
        const nowDayStart = getStartOfDay(now);

        return eventDayStart === nowDayStart && eventEnd > now;
      },
    },
  },
  Tasks: {
    assignment: {
      start(taskId: number, currentAssignmentId: number | undefined): boolean {
        // alternative: task.getInstances().stream().map(TaskInstance::getStatus).anyMatch(status -> status.equals(TaskInstanceStatus.ACTIVE));
        return !ACTION_VISIBILITY_LOGIC.Tasks.assignment.stop(taskId, currentAssignmentId);
      },

      restart(taskId: number, currentAssignmentId: number | undefined): boolean {
        return ACTION_VISIBILITY_LOGIC.Tasks.assignment.stop(taskId, currentAssignmentId);
      },

      stop(taskId: number, currentAssignmentId: number | undefined): boolean {
        return taskId === currentAssignmentId;
      },

      edit(): boolean {
        return true;
      },

      markDone(taskStatus: TaskStatus): boolean {
        return taskStatus !== TaskStatus.COMPLETE;
      },

      markIncomplete(taskStatus: TaskStatus): boolean {
        return taskStatus === TaskStatus.COMPLETE;
      },

      logWork(): boolean {
        return true;
      },

      addTime(): boolean {
        return true;
      },

      reset(exceptions: unknown[] | undefined): boolean {
        return !!exceptions?.length;
      },

      moveToTop(taskIndex: number, minIndex: number): boolean {
        return typeof taskIndex !== "number" || taskIndex !== minIndex;
      },

      delete(): boolean {
        return true;
      },
    },
    instances: {
      lockOrUnlock(taskInstanceStatus: TaskInstanceStatus): boolean {
        return taskInstanceIsUpcoming(taskInstanceStatus);
      },

      rescheduleType(eventEnd: Date, now: Date): "quick" | "snoozeOptions" {
        return eventEnd >= now ? "snoozeOptions" : "quick";
      },

      rescheduleWithSnooze(taskInstanceEnd: Date, now: Date): boolean {
        return taskInstanceEnd > now;
      },

      snoozeOptions(): readonly SnoozeOption[] {
        return SNOOZE_INTERVALS;
      },
    },
  },
  OneOnOnes: {
    assignment: {
      reset(exceptions: unknown[] | undefined): boolean {
        return !!exceptions?.length;
      },
    },
    instances: {
      lockOrUnlock(eventStart: Date, now: Date): boolean {
        return eventStart > now;
      },

      reschedule(): boolean {
        return true;
      },

      skipOptions(): readonly OneOnOneSkipOption[] {
        return ["ANOTHER_TIME", "ANOTHER_DAY", "ANOTHER_WEEK"];
      },
    },
  },
};
